import { useTranslation } from "react-i18next";
import { QuestionMarkCircleIcon } from "../ui/icons";

// Web port of ChurchServiceCardView from the iOS app
// (Salt-iOS/Features/LibraryFeature/.../View/Component/ChurchServiceCardView.swift),
// as used by FeaturedServiceView on the home screen.
//
// Props mirror the Swift initializer so this stays easy to diff against the
// app as both evolve. The card is presentational — callers own navigation.

export interface ChurchServiceCardProps {
  thumbnailUrl: string | null;
  title: string;
  description: string | null;
  /** When null the Discussion Questions action is hidden, matching iOS. */
  discussionQuestions: string | null;
  onDiscussionQuestionsClick: () => void;
  onDetailsClick: () => void;
}

// Card in SaltUIFoundation: white, 20pt continuous corner radius, and an
// ambient shadow (black 15%, radius 10, no offset).
//
// The card caps at 500px and centres beyond that. Below it, the wrapper's
// horizontal padding keeps the card (and its shadow) off the viewport edge
// while letting the card itself shrink — mirroring the app's screen margins.
const CARD =
  "relative isolate mx-auto w-full max-w-[500px] overflow-hidden rounded-[20px] bg-surface shadow-[0_0_20px_rgba(0,0,0,0.15)]";

// iOS imageHeight = 200.
const IMAGE_HEIGHT = "h-[200px]";

export default function ChurchServiceCard(props: ChurchServiceCardProps) {
  const { t } = useTranslation("serviceCard");

  return <div className="w-full px-4">
    <article className={CARD}>
      <div className={`relative ${IMAGE_HEIGHT} w-full`}>
        {props.thumbnailUrl
          ? <img
              src={props.thumbnailUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          // iOS falls back to a flat Color.gray when there is no thumbnail.
          : <div className="absolute inset-0 bg-gray-500" />}

        {/* LinearGradient(.clear -> .black.opacity(0.65), top -> bottom) */}
        <div className="absolute inset-0 bg-linear-to-b from-transparent to-black/65" />

        <div className="absolute inset-x-0 bottom-0 px-4 pb-3 text-white">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
            {t("featuredSermon")}
          </p>
          <h2 className="text-[22px] font-bold leading-tight">{props.title}</h2>
        </div>
      </div>

      <div className="flex flex-col gap-2 px-4 pt-4 pb-4">
        {props.description && (
          // iOS: .lineLimit(6) with 75% opacity.
          <p className="line-clamp-6 text-[17px] leading-normal text-gray-900/75">
            {props.description}
          </p>
        )}

        {/* ViewThatFits(in: .horizontal): a row when the actions fit, otherwise
            stacked. flex-wrap reproduces that without measuring. */}
        <div className="relative z-10 flex flex-wrap items-start gap-2">
          {props.discussionQuestions !== null && (
            <BorderedButton onClick={props.onDiscussionQuestionsClick}>
              <QuestionMarkCircleIcon className="size-[18px] shrink-0" />
              {t("discussionQuestions")}
            </BorderedButton>
          )}

          <BorderedButton onClick={props.onDetailsClick}>
            {t("details")}
            <ChevronForwardIcon />
          </BorderedButton>
        </div>
      </div>

      {/* In the app the whole card is a Button that opens the service detail.
          This overlay reproduces that: positioned (so it paints over the static
          image and description) but below the action buttons, which sit at
          z-10. It comes last in the DOM so the visible content is read first. */}
      <button
        type="button"
        onClick={props.onDetailsClick}
        aria-label={props.title}
        className="absolute inset-0 z-0 cursor-pointer"
      />
    </article>
  </div>
}

// Approximates SwiftUI's .buttonStyle(.bordered): a tinted capsule that takes
// its colour from the accent tint.
function BorderedButton(props: {
  onClick: () => void,
  children: React.ReactNode
}) {
  return <button
    type="button"
    onClick={props.onClick}
    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent/15 px-3 py-2 text-[17px] font-medium text-accent transition-opacity duration-200 hover:opacity-75"
  >
    {props.children}
  </button>
}

// SF Symbol: chevron.forward
function ChevronForwardIcon() {
  return <svg viewBox="0 0 16 16" aria-hidden="true" className="size-[15px] shrink-0">
    <path
      d="M5.5 2.5 11 8l-5.5 5.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round" />
  </svg>
}
